import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { axiosPublic } from '../../api/axios';
import { uploadFileToS3 } from '../../utils/s3Uploader'; // <-- Ensure this utility exists
import Header2 from '../../components/shared/Header2';
import Footer2 from '../../components/shared/Footer2';
import top3 from '../../assets/new-image/new-bg.png';

function JobApply() {
  const { jobid } = useParams();
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Fetch job details
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await axiosPublic.get(`/job-role/${jobid}`);
        setJobDetails(response.data.result);
      } catch (error) {
        console.error('Error fetching job details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobid]);

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      aboutMe: '',
      experience: '',
      noticePeriod: '',
      cv: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Required'),
      email: Yup.string().email('Invalid email address').required('Required'),
      phone: Yup.string().required('Required'),
      aboutMe: Yup.string().required('Required'),
      experience: Yup.number().min(0).required('Required'),
      noticePeriod: Yup.number().min(0).required('Required'),
      cv: Yup.mixed().required('Please upload your CV'),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setSubmitting(true);
        const s3Url = await uploadFileToS3(values.cv);

        const payload = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          aboutMe: values.aboutMe,
          experience: values.experience,
          noticePeriod: values.noticePeriod,
          cvUrl: s3Url,
          jobId: jobid,
        };

        const response = await axiosPublic.post(`/job-role/${jobid}/apply`, payload);

        if (response.data.success) {
          toast.success(response.data.message || 'Application submitted successfully!');

          // Optional: reset form
          resetForm({ values: {} });

          // ✅ Reload after short delay so user sees the toast
          setTimeout(() => {
            window.location.reload();
          }, 3000); // waits 2 seconds
        } else {
          toast.error(response.data.message || 'Submission failed. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        const msg = error.response?.data?.message || 'Something went wrong. Please try again.';
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      <Header2 />
      <section className="bg-[#FBFBFB] py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-0">
          <div className="flex flex-col lg:flex-row gap-12 items-start relative">
            <div className="top-image-1 lg:block hidden w-full absolute lg:-top-[20%] left-0 z-[-1]">
              <img src={top3} className="w-full h-auto object-contain" alt="" />
            </div>

            {/* Left Side - Job Details */}
            {loading ? (
              <div className="w-full lg:w-1/2">
                <p className="text-base text-gray-500">Loading job details...</p>
              </div>
            ) : jobDetails ? (
              <>

                <div className="w-full lg:w-1/2">
                  <h2 className="text-2xl lg:text-4xl font-semibold text-[#191919] mb-4">{jobDetails.name}</h2>
                  <p className="text-base text-[#40566D] mb-2"><strong>Description:</strong></p>
                  <p className="text-base text-[#626262] leading-relaxed">{jobDetails.description}</p>
                </div>
                {/* Right Side - Application Form */}
                <div className="w-full lg:w-1/2 bg-white p-8 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold text-[#191919] mb-6">Apply Now</h3>
                  <form onSubmit={formik.handleSubmit} className="space-y-6">

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1">Name</label>
                      <input
                        type="text"
                        name="name"
                        onChange={formik.handleChange}
                        value={formik.values.name}
                        onBlur={formik.handleBlur}
                        className="w-full border border-gray-300 rounded-md px-4 py-2"
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="text-red-500 text-sm mt-1">{formik.errors.name}</div>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        onChange={formik.handleChange}
                        value={formik.values.phone}
                        onBlur={formik.handleBlur}
                        className="w-full border border-gray-300 rounded-md px-4 py-2"
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <div className="text-red-500 text-sm mt-1">{formik.errors.phone}</div>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        onChange={formik.handleChange}
                        value={formik.values.email}
                        onBlur={formik.handleBlur}
                        className="w-full border border-gray-300 rounded-md px-4 py-2"
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>
                      )}
                    </div>

                    {/* About Me */}
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1">About Me</label>
                      <textarea
                        name="aboutMe"
                        rows="4"
                        onChange={formik.handleChange}
                        value={formik.values.aboutMe}
                        onBlur={formik.handleBlur}
                        className="w-full border border-gray-300 rounded-md px-4 py-2 resize-none"
                      />
                      {formik.touched.aboutMe && formik.errors.aboutMe && (
                        <div className="text-red-500 text-sm mt-1">{formik.errors.aboutMe}</div>
                      )}
                    </div>

                    {/* Experience */}
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1">Experience (in years)</label>
                      <input
                        type="number"
                        name="experience"
                        onChange={formik.handleChange}
                        value={formik.values.experience}
                        onBlur={formik.handleBlur}
                        className="w-full border border-gray-300 rounded-md px-4 py-2"
                      />
                      {formik.touched.experience && formik.errors.experience && (
                        <div className="text-red-500 text-sm mt-1">{formik.errors.experience}</div>
                      )}
                    </div>

                    {/* Notice Period */}
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1">Notice Period (in days)</label>
                      <input
                        type="number"
                        name="noticePeriod"
                        onChange={formik.handleChange}
                        value={formik.values.noticePeriod}
                        onBlur={formik.handleBlur}
                        className="w-full border border-gray-300 rounded-md px-4 py-2"
                      />
                      {formik.touched.noticePeriod && formik.errors.noticePeriod && (
                        <div className="text-red-500 text-sm mt-1">{formik.errors.noticePeriod}</div>
                      )}
                    </div>

                    {/* Upload CV */}
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1">Upload CV</label>
                      <input
                        type="file"
                        name="cv"
                        onChange={(event) => {
                          formik.setFieldValue("cv", event.currentTarget.files[0]);
                        }}
                        onBlur={formik.handleBlur}
                        className="w-full border border-gray-300 rounded-md px-4 py-2 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#FFC727] file:text-[#191919] hover:file:bg-[#e6b622]"
                      />
                      {formik.touched.cv && formik.errors.cv && (
                        <div className="text-red-500 text-sm mt-1">{formik.errors.cv}</div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#FFC727] text-[#191919] font-semibold py-3 rounded-md hover:bg-[#e6b622] transition-colors"
                    >
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </button>

                  </form>
                </div>
              </>
            ) : (
              <div className="w-full lg:w-1/2">
                <p className="text-base text-red-500">Job not found or failed to load.</p>
              </div>
            )}


          </div>
        </div>
      </section>
      <Footer2 />
    </>
  );
}

export default JobApply;
